import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import WhatsAppButton from '../../../components/whatsapp-button/ui/WhatsAppButton';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import { useLanguage } from '../../../../context/LanguageContext';
import { getDocs } from 'firebase/firestore';
import { getClientCollection } from '../../../../services/FirebaseService';
import { addToWishlist } from '../../../../utils/wishlistUtils';
import { seoData } from '../../../../data/seoData';
import '../style/mobility.css';

interface Category {
    id: number;
    name: string;
}

const MobilityPage: React.FC = () => {
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [products, setProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [categories, setCategories] = useState<Category[]>([]);

    // Load SubcategoryEnum script and initialize categories
    useEffect(() => {
        let retryCount = 0;
        const maxRetries = 30; // 3 seconds max
        
        const loadAndInitializeCategories = () => {
            // Check if SubcategoryEnum is already loaded
            if (typeof (window as any).SubcategoryEnum !== 'undefined') {
                // Initialize subcategoryEnum instance (like original code)
                if (!(window as any).subcategoryEnum) {
                    try {
                        (window as any).subcategoryEnum = new (window as any).SubcategoryEnum();
                    } catch (_) {
                        return;
                    }
                }
                
                // Populate categories
                try {
                    const subcategoryEnum = (window as any).subcategoryEnum;
                    if (!subcategoryEnum || typeof subcategoryEnum.getAllSubcategories !== 'function') {
                        if (retryCount < maxRetries) {
                            retryCount++;
                            setTimeout(loadAndInitializeCategories, 100);
                        }
                        return;
                    }
                    
                    const subcategories = subcategoryEnum.getAllSubcategories(language);
                    
                    
                    if (!subcategories || Object.keys(subcategories).length === 0) {
                        return;
                    }
                    
                    const categoryArray: Category[] = Object.entries(subcategories).map(([id, name]) => ({
                        id: parseInt(id),
                        name: name as string
                    }));
                    
                    setCategories(categoryArray);
                } catch (_) {
                }
                return;
            }

            // Load SubcategoryEnum script if not loaded
            const existingScript = document.querySelector('script[src="/data/enum/SubcategoryEnum.js"]');
            if (!existingScript) {
                const script = document.createElement('script');
                script.src = '/data/enum/SubcategoryEnum.js';
                script.onload = () => {
                    // Wait a bit for the class to be available
                    setTimeout(() => {
                        if (typeof (window as any).SubcategoryEnum !== 'undefined') {
                            try {
                                (window as any).subcategoryEnum = new (window as any).SubcategoryEnum();
                                
                                // Populate categories after initialization
                                const subcategoryEnum = (window as any).subcategoryEnum;
                                if (subcategoryEnum && typeof subcategoryEnum.getAllSubcategories === 'function') {
                                    const subcategories = subcategoryEnum.getAllSubcategories(language);
                                    
                                    
                                    const categoryArray: Category[] = Object.entries(subcategories).map(([id, name]) => ({
                                        id: parseInt(id),
                                        name: name as string
                                    }));
                                    
                                    setCategories(categoryArray);
                                } else {
                                }
                            } catch (_) {
                            }
                        } else {
                        }
                    }, 100);
                };
                script.onerror = () => {
                };
                document.head.appendChild(script);
            } else {
                // Script already exists, wait a bit and retry
                if (retryCount < maxRetries) {
                    retryCount++;
                    setTimeout(loadAndInitializeCategories, 100);
                } else {
                }
            }
        };

        loadAndInitializeCategories();
    }, [language]);

    // Load products from Firebase
    useEffect(() => {
        const loadProducts = async () => {
            try {
                setLoading(true);
                
                // Get all products
                const allProductsSnapshot = await getDocs(getClientCollection('products'));
                
                // Filter to only include products with categoryId === 10 (mobility)
                const mobilityProducts = allProductsSnapshot.docs
                    .map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }))
                    .filter((product: any) => 
                        product.categoryId && product.categoryId.toString() === '10'
                    );
                
                // Sort by createdAt (newest first)
                mobilityProducts.sort((a: any, b: any) => {
                    const dateA = a.createdAt ? (a.createdAt.seconds ? new Date(a.createdAt.seconds * 1000) : new Date(a.createdAt)) : new Date(0);
                    const dateB = b.createdAt ? (b.createdAt.seconds ? new Date(b.createdAt.seconds * 1000) : new Date(b.createdAt)) : new Date(0);
                    return dateB.getTime() - dateA.getTime();
                });
                
                setProducts(mobilityProducts);
                setLoading(false);
            } catch (_) {
                setLoading(false);
            }
        };

        loadProducts();
    }, []);

    // Ensure theme is preserved on mount
    useEffect(() => {
        // Scroll to top on mount
        window.scrollTo(0, 0);
        
        // Theme is managed by ThemeContext - don't manipulate it directly
        // The ThemeContext will apply the theme automatically
    }, []);

    const filteredProducts = products.filter(product => {
        const matchesSearch = searchTerm === '' || 
            product.productNamePt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.productNameEn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.productCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.productBrand?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = selectedCategory === 'all' || 
            product.subcategoryId?.toString() === selectedCategory;
        
        return matchesSearch && matchesCategory;
    });

    return (
        <>
            <SEO 
                title={seoData.mobility.title}
                description={seoData.mobility.description}
                keywords={seoData.mobility.keywords}
                canonical={seoData.mobility.canonical}
            />
        <div className="mobility-page">
            <Navbar />
            
            {/* Page Header */}
            <section className="page-header">
                <div
                    className="page-header__bg"
                    style={{
                        backgroundImage: 'url(/assets/logo/mobility/mobility_2.webp)',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        height: '680px'
                    }}
                ></div>
                <div className="container">
                    <div className="page-header__inner">
                        <div className="page-header__img-1">
                            <img loading="lazy" 
                                src="/assets/logo/mobility/mobility_1.webp" 
                                alt="Roady Oliveira de Azeméis" 
                                width="343" 
                                height="430"
                            />
                        </div>
                        <h3>{t.NAVIGATION?.mobility || 'Mobilidade'}</h3>
                        <div className="thm-breadcrumb__inner">
                            <ul className="thm-breadcrumb list-unstyled">
                                <li><Link to="/">{t.NAVIGATION?.start || 'Início'}</Link></li>
                                <li><span className="fas fa-angle-right"></span></li>
                                <li>{t.NAVIGATION?.mobility || 'Mobilidade'}</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>
            
            {/* Product Section */}
            <section className="product">
                <div className="container">
                    <div className="row">
                        {/* Sidebar */}
                        <div className="col-xl-3 col-lg-12">
                            <div className="product__sidebar">
                                {/* Search */}
                                <div className="shop-search product__sidebar-single">
                                    <form onSubmit={(e) => e.preventDefault()}>
                                        <input 
                                            type="text" 
                                            placeholder={language === 'pt' ? 'Pesquisar' : 'Search'}
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                        <button type="submit"><i className="fa fa-search"></i></button>
                                    </form>
                                </div>

                                {/* Categories */}
                                <div className="shop-category product__sidebar-single">
                                    <h3 className="product__sidebar-title">
                                        {language === 'pt' ? 'Categorias' : 'Categories'}
                                    </h3>
                                    <ul className="list-unstyled" id="categoryFilter">
                                        <li className={selectedCategory === 'all' ? 'active' : ''}>
                                            <a 
                                                href="#" 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setSelectedCategory('all');
                                                }}
                                            >
                                                {language === 'pt' ? 'Todas as Categorias' : 'All Categories'}
                                            </a>
                                        </li>
                                        {categories.length > 0 ? (
                                            categories.map((category) => (
                                                <li 
                                                    key={category.id}
                                                    className={selectedCategory === category.id.toString() ? 'active' : ''}
                                                >
                                                    <a 
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setSelectedCategory(category.id.toString());
                                                        }}
                                                        data-category={category.id}
                                                    >
                                                        {category.name}
                                                    </a>
                                                </li>
                                            ))
                                        ) : (
                                            <li>
                                                <span style={{ color: '#999', fontStyle: 'italic' }}>
                                                    {language === 'pt' ? 'A carregar categorias...' : 'Loading categories...'}
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Product Grid */}
                        <div className="col-xl-9 col-lg-12">
                            <div className="product__items">
                                <div className="row">
                                    <div className="col-xl-12">
                                        <div className="product__showing-result">
                                            <div className="product__showing-text-box">
                                                <p className="product__showing-text">
                                                    {loading 
                                                        ? (language === 'pt' ? 'A carregar produtos...' : 'Loading products...')
                                                        : (language === 'pt' 
                                                            ? `Mostrando ${filteredProducts.length} resultados`
                                                            : `Showing ${filteredProducts.length} results`
                                                        )
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="product__all">
                                    <div className="product__all-tab">
                                        <div className="product__all-tab-button">
                                            <ul className="tabs-button-box clearfix">
                                                <li 
                                                    className={`tab-btn-item ${viewMode === 'grid' ? 'active-btn-item' : ''}`}
                                                    onClick={() => setViewMode('grid')}
                                                >
                                                    <div className="product__all-tab-button-icon one">
                                                        <i className="fa fa-solid fa-bars"></i>
                                                    </div>
                                                </li>
                                                <li 
                                                    className={`tab-btn-item ${viewMode === 'list' ? 'active-btn-item' : ''}`}
                                                    onClick={() => setViewMode('list')}
                                                >
                                                    <div className="product__all-tab-button-icon">
                                                        <i className="fa fa-solid fa-list-ul"></i>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* Tabs Content */}
                                        <div className="tabs-content-box">
                                            <div className={`tab-content-box-item ${viewMode === 'grid' ? 'tab-content-box-item-active' : ''}`}>
                                                <div className="product__all-tab-content-box-item">
                                                    <div className="product__all-tab-single">
                                                        <div className="row">
                                                            {loading ? (
                                                                <div className="col-12 text-center">
                                                                    <div className="spinner-border text-primary" role="status">
                                                                        <span className="visually-hidden">
                                                                            {language === 'pt' ? 'A carregar produtos...' : 'Loading products...'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="mt-2">
                                                                        {language === 'pt' ? 'A carregar produtos...' : 'Loading products...'}
                                                                    </p>
                                                                </div>
                                                            ) : filteredProducts.length === 0 ? (
                                                                <div className="col-12 text-center">
                                                                    <p>
                                                                        {language === 'pt' ? 'Nenhum produto encontrado' : 'No products found'}
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                filteredProducts.map((product) => {
                                                                    // Handle productImages exactly like original code
                                                                    const productImage = product.productImages && product.productImages.length > 0 
                                                                        ? product.productImages[0] 
                                                                        : 'assets/images/shop/shop-product-1-1.jpg';
                                                                    
                                                                    const productName = language === 'pt' 
                                                                        ? (product.productNamePt || product.productNameEn || 'Product')
                                                                        : (product.productNameEn || product.productNamePt || 'Product');
                                                                    
                                                                    return (
                                                                        <div key={product.id} className="col-xl-4 col-lg-4 col-md-6">
                                                                            <div 
                                                                                className="single-product-style1" 
                                                                                style={{ cursor: 'pointer' }}
                                                                                onClick={(e) => {
                                                                                    // Don't navigate if clicking on wishlist or quick view buttons
                                                                                    if ((e.target as HTMLElement).closest('.add-to-wishlist-btn') || 
                                                                                        (e.target as HTMLElement).closest('.quick-view-btn')) {
                                                                                        return;
                                                                                    }
                                                                                    navigate(`/produto/${product.id}`);
                                                                                }}
                                                                            >
                                                                                <div className="single-product-style1__img">
                                                                                    <img loading="lazy" src={productImage} alt={productName} />
                                                                                    <img loading="lazy" src={productImage} alt={productName} />
                                                                                    <ul className="single-product-style1__info">
                                                                                        <li>
                                                                                            <a 
                                                                                                href="#" 
                                                                                                className="add-to-wishlist-btn" 
                                                                                                data-product-id={product.id} 
                                                                                                title="Add to Wishlist"
                                                                                                onClick={(e) => {
                                                                                                    e.preventDefault();
                                                                                                    e.stopPropagation();
                                                                                                    addToWishlist(product, language);
                                                                                                }}
                                                                                            >
                                                                                                <i className="fa fa-regular fa-heart"></i>
                                                                                            </a>
                                                                                        </li>
                                                                                        <li>
                                                                                            <a 
                                                                                                href="#" 
                                                                                                className="quick-view-btn" 
                                                                                                data-product-id={product.id} 
                                                                                                title="Quick View"
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                            >
                                                                                                <i className="fa fa-regular fa-eye"></i>
                                                                                            </a>
                                                                                        </li>
                                                                                    </ul>
                                                                                </div>
                                                                                <div className="single-product-style1__content">
                                                                                    <div className="single-product-style1__content-left">
                                                                                        <h4>
                                                                                            <Link 
                                                                                                to={`/produto/${product.id}`}
                                                                                                onClick={(e) => e.stopPropagation()}
                                                                                            >
                                                                                                {productName}
                                                                                            </Link>
                                                                                        </h4>
                                                                                    </div>
                                                                                    <div className="single-product-style1__content-right">
                                                                                        <div className="single-product-style1__brand">
                                                                                            <p>{product.productBrand || 'Brand'}</p>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={`tab-content-box-item ${viewMode === 'list' ? 'tab-content-box-item-active' : ''}`}>
                                                <div className="product__all-tab-content-box-item">
                                                    <div className="product__all-tab-single">
                                                        <div className="row">
                                                            {loading ? (
                                                                <div className="col-12 text-center">
                                                                    <div className="spinner-border text-primary" role="status">
                                                                        <span className="visually-hidden">
                                                                            {language === 'pt' ? 'A carregar produtos...' : 'Loading products...'}
                                                                        </span>
                                                                    </div>
                                                                    <p className="mt-2">
                                                                        {language === 'pt' ? 'A carregar produtos...' : 'Loading products...'}
                                                                    </p>
                                                                </div>
                                                            ) : filteredProducts.length === 0 ? (
                                                                <div className="col-12 text-center">
                                                                    <p>
                                                                        {language === 'pt' ? 'Nenhum produto encontrado' : 'No products found'}
                                                                    </p>
                                                                </div>
                                                            ) : (
                                                                filteredProducts.map((product) => {
                                                                    // Handle productImages exactly like original code
                                                                    const productImage = product.productImages && product.productImages.length > 0 
                                                                        ? product.productImages[0] 
                                                                        : 'assets/images/shop/shop-product-2-1.jpg';
                                                                    
                                                                    const productName = language === 'pt' 
                                                                        ? (product.productNamePt || product.productNameEn || 'Product')
                                                                        : (product.productNameEn || product.productNamePt || 'Product');
                                                                    
                                                                    return (
                                                                        <div key={product.id} className="col-12">
                                                                            <div 
                                                                                className="single-product-style2"
                                                                                style={{ cursor: 'pointer' }}
                                                                                onClick={(e) => {
                                                                                    // Don't navigate if clicking on wishlist or quick view buttons
                                                                                    if ((e.target as HTMLElement).closest('.add-to-wishlist-btn') || 
                                                                                        (e.target as HTMLElement).closest('.quick-view-btn')) {
                                                                                        return;
                                                                                    }
                                                                                    navigate(`/produto/${product.id}`);
                                                                                }}
                                                                            >
                                                                                <div className="row">
                                                                                    <div className="col-xl-6 col-lg-6 col-md-6">
                                                                                        <div className="single-product-style2__img">
                                                                                            <img loading="lazy" src={productImage} alt={productName} />
                                                                                            <img loading="lazy" src={productImage} alt={productName} />
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className="col-xl-6 col-lg-6 col-md-6">
                                                                                        <div className="single-product-style2__content">
                                                                                            <div className="single-product-style2__text">
                                                                                                <h4>
                                                                                                    <Link 
                                                                                                        to={`/produto/${product.id}`}
                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                    >
                                                                                                        {productName}
                                                                                                    </Link>
                                                                                                </h4>
                                                                                                <p className="product-brand">
                                                                                                    {language === 'pt' ? 'Marca: ' : 'Brand: '}
                                                                                                    {product.productBrand || 'N/A'}
                                                                                                </p>
                                                                                            </div>
                                                                                            <ul className="single-product-style2__info">
                                                                                                <li>
                                                                                                    <a 
                                                                                                        href="#" 
                                                                                                        className="add-to-wishlist-btn" 
                                                                                                        data-product-id={product.id} 
                                                                                                        title="Add to Wishlist"
                                                                                                        onClick={(e) => {
                                                                                                            e.preventDefault();
                                                                                                            e.stopPropagation();
                                                                                                            addToWishlist(product, language);
                                                                                                        }}
                                                                                                    >
                                                                                                        <i className="fa fa-regular fa-heart"></i>
                                                                                                    </a>
                                                                                                </li>
                                                                                                <li>
                                                                                                    <a 
                                                                                                        href="#" 
                                                                                                        className="quick-view-btn" 
                                                                                                        data-product-id={product.id} 
                                                                                                        title="Quick View"
                                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                                    >
                                                                                                        <i className="fa fa-regular fa-eye"></i>
                                                                                                    </a>
                                                                                                </li>
                                                                                            </ul>
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Pagination */}
                                    <ul className="styled-pagination text-center clearfix list-unstyled">
                                        <li className="arrow prev active">
                                            <a href="#"><span className="fas fa-angle-left"></span></a>
                                        </li>
                                        <li><a href="#">1</a></li>
                                        <li><a href="#">2</a></li>
                                        <li><a href="#">3</a></li>
                                        <li className="arrow next">
                                            <a href="#"><span className="fas fa-angle-right"></span></a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <Footer />
            <WhatsAppButton />
            <ScrollToTop />
        </div>
        </>
    );
};

export default MobilityPage;
